import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Headset,
    MessageSquare,
    Paperclip,
    RefreshCw,
    Send,
    Shield,
    Trash2,
    Truck,
} from "lucide-react";
import { toast } from "sonner";
import { absoluteUrl, filoChat } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function dedupeMessages(list) {
    const seen = new Set();
    return list.filter((m) => {
        const key = m.id ? `id:${m.id}` : `tmp:${m.tip}:${m.tarih}:${m.metin}:${m.dosya_url}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

export default function FiloCanliDestekPage() {
    const [odalar, setOdalar] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [clearing, setClearing] = useState(false);
    const chatRef = useRef(null);
    const fileRef = useRef(null);
    const sendingRef = useRef(false);
    const msgCountRef = useRef(0);

    const loadRooms = useCallback(async (silent = false) => {
        try {
            const data = await filoChat.listRooms();
            setOdalar(data.odalar || []);
        } catch (e) {
            if (!silent) toast.error("Chat odaları yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadMessages = useCallback(async (odaId, opts = {}) => {
        const { silent = false, setActiveMeta = true } = opts;
        try {
            const data = await filoChat.getMessages(odaId);
            setMessages(dedupeMessages(data.mesajlar || []));
            if (setActiveMeta) {
                setActive({
                    odaId: data.oda_id,
                    filoId: data.filo_id,
                    filoAdi: data.filo_adi,
                });
            }
        } catch (e) {
            if (!silent) toast.error("Mesajlar yüklenemedi.");
        }
    }, []);

    useEffect(() => {
        loadRooms();
        const t = setInterval(() => loadRooms(true), 20000);
        return () => clearInterval(t);
    }, [loadRooms]);

    useEffect(() => {
        if (!active?.odaId) return undefined;
        const odaId = active.odaId;
        loadMessages(odaId, { silent: true, setActiveMeta: false });
        const t = setInterval(
            () => loadMessages(odaId, { silent: true, setActiveMeta: false }),
            8000,
        );
        return () => clearInterval(t);
    }, [active?.odaId, loadMessages]);

    useEffect(() => {
        const count = messages.length;
        if (count > msgCountRef.current && chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
        msgCountRef.current = count;
    }, [messages]);

    const appendMessage = useCallback((msg) => {
        if (!msg) return;
        setMessages((prev) => dedupeMessages([...prev, msg]));
    }, []);

    const handleSend = async () => {
        const text = draft.trim();
        if (!text || !active?.odaId || sendingRef.current) return;
        sendingRef.current = true;
        setSending(true);
        try {
            const res = await filoChat.send(active.odaId, text);
            setDraft("");
            appendMessage(res.mesaj);
            loadRooms(true);
        } catch (e) {
            toast.error(e?.response?.data?.error || "Mesaj gönderilemedi.");
        } finally {
            sendingRef.current = false;
            setSending(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !active?.odaId || sendingRef.current) return;
        sendingRef.current = true;
        setUploading(true);
        try {
            const res = await filoChat.uploadFile(
                active.odaId,
                file,
                draft.trim(),
            );
            setDraft("");
            appendMessage(res.mesaj);
            loadRooms(true);
            toast.success("Dosya gönderildi.");
        } catch (err) {
            toast.error(
                err?.response?.data?.error || "Dosya gönderilemedi.",
            );
        } finally {
            sendingRef.current = false;
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const handleClearChat = async () => {
        if (!active?.odaId || clearing) return;
        const ok = window.confirm(
            `${active.filoAdi} ile olan tüm sohbet geçmişi silinecek. Emin misiniz?`,
        );
        if (!ok) return;
        setClearing(true);
        try {
            const res = await filoChat.clearRoom(active.odaId);
            setMessages([]);
            msgCountRef.current = 0;
            loadRooms(true);
            toast.success(`${res.deleted || 0} mesaj silindi.`);
        } catch (e) {
            toast.error("Sohbet temizlenemedi.");
        } finally {
            setClearing(false);
        }
    };

    const totalUnread = odalar.reduce((n, o) => n + (o.unread_count || 0), 0);

    return (
        <div className="p-4 lg:p-6 flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden">
            {/* Hero — sabit yükseklik */}
            <header className="shrink-0 relative overflow-hidden rounded-2xl border border-[rgba(201,169,97,0.22)] bg-gradient-to-br from-[#111c4a] via-[#0b1437] to-[#1a1020] p-5 lg:p-6 mb-4">
                <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-[rgba(201,169,97,0.35)] bg-[rgba(139,30,45,0.25)] text-[#e6cf94]">
                            <Headset className="h-6 w-6" />
                        </span>
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#c9a961] mb-0.5">
                                Filo operasyon · Canlı destek
                            </p>
                            <h1 className="font-display text-xl lg:text-2xl gold-text">
                                Filo Canlı Destek Merkezi
                            </h1>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-center min-w-[90px]">
                            <p className="text-[9px] uppercase tracking-widest text-[#8b93ae]">Oda</p>
                            <p className="text-lg font-display text-[#f5ecd6]">{odalar.length}</p>
                        </div>
                        <div className="rounded-lg border border-[rgba(139,30,45,0.35)] bg-[rgba(139,30,45,0.15)] px-3 py-2 text-center min-w-[90px]">
                            <p className="text-[9px] uppercase tracking-widest text-[#f0a8b4]">Okunmamış</p>
                            <p className="text-lg font-display text-white">{totalUnread}</p>
                        </div>
                    </div>
                </div>
            </header>

            {/* Ana grid — kalan yüksekliği doldurur, taşmaz */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 flex-1 min-h-0">
                <aside className="xl:col-span-4 rounded-2xl border border-[rgba(201,169,97,0.15)] bg-[#111c4a]/80 overflow-hidden flex flex-col min-h-0 shadow-xl shadow-black/20">
                    <div className="shrink-0 px-4 py-3 border-b border-white/10 flex items-center justify-between bg-[rgba(11,20,55,0.6)]">
                        <div className="flex items-center gap-2">
                            <MessageSquare className="h-4 w-4 text-[#c9a961]" />
                            <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-[#e6cf94]">
                                Aktif odalar
                            </h2>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => loadRooms()}
                            disabled={loading}
                            className="text-[#c9a961] hover:text-[#f5ecd6] hover:bg-white/5 h-8 w-8 p-0"
                        >
                            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        </Button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto">
                        {odalar.length === 0 && !loading && (
                            <p className="p-6 text-sm text-[#8b93ae] text-center">
                                Henüz aktif chat odası yok.
                            </p>
                        )}
                        {odalar.map((oda) => {
                            const selected = active?.odaId === oda.id;
                            return (
                                <button
                                    key={oda.id}
                                    type="button"
                                    onClick={() => loadMessages(oda.id)}
                                    className={`w-full text-left px-4 py-3 transition border-l-[3px] ${
                                        selected
                                            ? "bg-gradient-to-r from-[rgba(139,30,45,0.2)] to-transparent border-[#c9a961]"
                                            : "border-transparent hover:bg-white/[0.03]"
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <span className="font-semibold text-sm text-[#f5ecd6] truncate">
                                            {oda.filo_adi}
                                        </span>
                                        {oda.unread_count > 0 && (
                                            <span className="shrink-0 min-w-[1.25rem] h-5 px-1 rounded-full bg-[#8b1e2d] text-white text-[10px] font-bold inline-flex items-center justify-center">
                                                {oda.unread_count}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-[#9aa3bf] mt-1 truncate">
                                        {oda.son_mesaj || "Bağlantı açık"}
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                </aside>

                <section className="xl:col-span-8 rounded-2xl border border-[rgba(201,169,97,0.15)] bg-[#0b1437]/90 overflow-hidden flex flex-col min-h-0 shadow-xl shadow-black/25">
                    {!active ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                            <Truck className="h-12 w-12 text-[#c9a961] opacity-40 mb-3" />
                            <h3 className="font-display text-lg text-[#f5ecd6]">Oda seçin</h3>
                            <p className="text-sm text-[#8b93ae] mt-1">Sol listeden bir filo seçin.</p>
                        </div>
                    ) : (
                        <>
                            {/* Sabit başlık */}
                            <div className="shrink-0 px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[rgba(139,30,45,0.12)] to-[rgba(17,28,74,0.8)] flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <Shield className="h-4 w-4 text-[#c9a961] shrink-0" />
                                        <h3 className="font-display text-base text-[#f5ecd6] truncate">
                                            {active.filoAdi}
                                        </h3>
                                    </div>
                                    <p className="text-[10px] text-emerald-400/90 mt-0.5 ml-6">
                                        ● Canlı kanal aktif
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={clearing || messages.length === 0}
                                    onClick={handleClearChat}
                                    className="shrink-0 border-rose-500/30 text-rose-300 hover:bg-rose-500/10 text-xs h-8"
                                >
                                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                                    Sohbeti temizle
                                </Button>
                            </div>

                            {/* Mesaj alanı — sabit, kaydırılabilir */}
                            <div
                                ref={chatRef}
                                className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3 bg-[radial-gradient(ellipse_at_top,rgba(201,169,97,0.04),transparent_50%)]"
                            >
                                {messages.length === 0 && (
                                    <p className="text-center text-sm text-[#8b93ae] py-8">
                                        Henüz mesaj yok.
                                    </p>
                                )}
                                {messages.map((m) => {
                                    const isAdmin = m.tip === "ADMIN";
                                    const fileUrl = absoluteUrl(m.dosya_url);
                                    return (
                                        <div
                                            key={m.id || `${m.tarih}-${m.metin}-${m.dosya_url}`}
                                            className={`flex flex-col max-w-[85%] ${
                                                isAdmin ? "ml-auto items-end" : "items-start"
                                            }`}
                                        >
                                            <div
                                                className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                                                    isAdmin
                                                        ? "bg-gradient-to-br from-[#8b1e2d] to-[#6b1522] text-[#fff8f0] rounded-br-sm border border-[rgba(201,169,97,0.25)]"
                                                        : "bg-[#111c4a] text-[#eef1fa] border border-[rgba(201,169,97,0.12)] rounded-bl-sm"
                                                }`}
                                            >
                                                {fileUrl ? (
                                                    <a
                                                        href={fileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-1.5 underline decoration-[#c9a961]/50 text-xs mb-1"
                                                    >
                                                        <Paperclip className="h-3 w-3 shrink-0" />
                                                        {m.dosya_adi || "Dosya eki"}
                                                    </a>
                                                ) : null}
                                                {m.metin ? (
                                                    <span className="whitespace-pre-wrap break-words">{m.metin}</span>
                                                ) : null}
                                            </div>
                                            <span className="text-[9px] text-[#7a839c] mt-1 uppercase tracking-wider">
                                                {isAdmin ? "MBS · " : "Filo · "}
                                                {m.tarih}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Sabit giriş alanı — alta yapışık, büyümez */}
                            <div className="shrink-0 px-3 py-3 border-t border-white/10 bg-[#0a1028]/90">
                                <div className="flex gap-2 items-end">
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        className="hidden"
                                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip"
                                        onChange={handleFileUpload}
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        disabled={uploading || sending}
                                        onClick={() => fileRef.current?.click()}
                                        className="shrink-0 h-9 w-9 border-[rgba(201,169,97,0.3)] text-[#c9a961]"
                                    >
                                        <Paperclip className="h-4 w-4" />
                                    </Button>
                                    <Textarea
                                        value={draft}
                                        onChange={(e) => setDraft(e.target.value)}
                                        placeholder="Mesaj yazın… (Enter gönder)"
                                        rows={2}
                                        className="flex-1 resize-none min-h-[52px] max-h-[52px] overflow-y-auto bg-[#111c4a] border-[rgba(201,169,97,0.15)] text-[#f5ecd6] text-sm py-2 focus-visible:ring-[#c9a961]/40"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSend();
                                            }
                                        }}
                                    />
                                    <Button
                                        onClick={handleSend}
                                        disabled={sending || uploading || !draft.trim()}
                                        className="shrink-0 h-9 w-9 p-0 bg-gradient-to-r from-[#8b1e2d] to-[#a83246] text-white"
                                    >
                                        <Send className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}
